import { Component } from '@angular/core'
import * as XLSX from 'xlsx'
import { CoreService, NotificationService } from '../../../../services'
import { DialogService } from 'primeng/dynamicdialog'
import { InvalidListComponent } from '../../dialogs/invalid-list/invalid-list.component'
import { Export, Product } from 'depoto-core/src/entities'
import { Emittable, Emitter } from '@ngxs-labs/emitter'
import { PlugplayState } from '../../states/plugplay-state.service'
import { OptionsConfig } from '../../../../configs'
import { TranslateService } from '@ngx-translate/core'
import { Router } from '@angular/router'
import { FileUtilService } from '../../../../utils/file-util.service'

@Component({
  selector: 'app-step5',
  templateUrl: './step5.component.html',
  styleUrls: ['./step5.component.scss'],
  providers: [DialogService],
})
export class Step5Component {
  @Emitter(PlugplayState.setActive)
  private readonly setActive: Emittable<number>
  @Emitter(PlugplayState.setHighestLastStepCompleted)
  public readonly setHighestLastStepCompleted: Emittable<number>
  @Emitter(PlugplayState.setHighestStepVisited)
  private readonly setHighestStepVisited: Emittable<number>
  private supplierList: Array<{ id: null; name: null }>
  public showSkipMessage: boolean
  public showValidationProgress: boolean
  public isValidationRunning: boolean
  public showProgress: boolean
  private xlsxData: any
  public validationProgressValue = 0
  public progressValue = 0
  exportLoading = true
  exportUrl = ''
  file: File
  arrayBuffer: any
  fileList: any
  eanMap = new Map<string, any>()
  showStart = false
  errorList = new Array<{ line: number; error: string }>()
  itemCount: number
  public showImport = true
  private vatList: Array<number> = []
  public isImportInProgress = false
  showResultPage = false
  importErrorMsgs: string[] = []

  constructor(
    public core: CoreService,
    public dialogService: DialogService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly fileUtil: FileUtilService,
  ) {
    this.setActive.emit(4)
    this.setHighestStepVisited.emit(4)

    this.core.services.supplier
      .getList({ page: 0, sort: 'id', direction: 'DESC' }, { id: null, name: null })
      .then(response => (this.supplierList = response.items))
    this.core.services.export
      .create(new Export({ type: 'hs-wizard-products' }), { id: null, url: null, file: { mimeType: null } })
      .then(value => this.fetchExport(value.id))
    this.core.services.vat
      .getList({}, { percent: null })
      .then(value => (this.vatList = value.items.map(vat => vat.percent)))
  }

  private fetchExport(id: number) {
    this.core.services.export.getById(id, { id: null, url: null }).then(response => {
      if (response.url) {
        this.exportLoading = false
        this.exportUrl = response.url
      } else {
        setTimeout(this.fetchExport.bind(this, id), 1000)
      }
    })
  }

  async downloadFile() {
    await this.fileUtil.downloadFile(this.exportUrl, 'product-import.xlsx')
  }

  async selected(event) {
    this.errorList = []
    this.file = event.addedFiles
      .filter(f => f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      .pop()
    if (this.file) {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(this.file)
      fileReader.onload = async e => {
        this.arrayBuffer = fileReader.result
        const data = new Uint8Array(this.arrayBuffer)
        const arr = []
        for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i])
        const bstr = arr.join('')
        const workbook = XLSX.read(bstr, { type: 'binary' })
        const first_sheet_name = workbook.SheetNames[0]
        const header: Array<string> = XLSX.utils
          .sheet_to_json(workbook.Sheets[first_sheet_name], { header: 1 })
          .shift() as Array<string>
        this.xlsxData = XLSX.utils.sheet_to_json(workbook.Sheets[first_sheet_name], { raw: true }).map(row => {
          const temp = {}
          header.forEach((key, index) => (temp[index] = row[key]))
          return temp
        })
        this.itemCount = this.xlsxData.length

        await this.validate(this.xlsxData)

        this.showStart = true
        this.showSkipMessage = true
      }
    }
  }

  private async validate(list: Array<any>): Promise<void> {
    this.showValidationProgress = true
    this.isValidationRunning = true
    let line = 0
    this.eanMap = new Map<string, any>()
    for await (const row of list) {
      const result = await this.errorType(row)
      if (result !== ERROR_TYPE.IS_EVERYTHING_OK) {
        this.errorList.push({ line, error: result })
      }
      if (`${row[COLUMNS.EAN]}`?.length > 0) {
        this.eanMap.set(`${row[COLUMNS.EAN]}`, row)
      }
      this.validationProgressValue = line + 1
      line++
    }
    setTimeout(() => (this.isValidationRunning = false), 3000)
  }

  private async errorType(line: any): Promise<ERROR_TYPE> {
    if (line[COLUMNS.PRODUCT_ID]) {
      // todo skip this check for now
      // if (!(await this.core.services.product.getById(line[COLUMNS.PRODUCT_ID], { id: null }))) {
      //   return ERROR_TYPE.PRODUCT
      // }
    }
    if (!line[COLUMNS.NAME]) {
      return ERROR_TYPE.NAME
    }
    const supplierDoesNotExist = this.supplierList.findIndex(value => value.name === `${line[COLUMNS.SUPPLIER]}`) === -1
    if (supplierDoesNotExist && !!line[COLUMNS.SUPPLIER]) {
      return ERROR_TYPE.SUPPLIER
    }
    if (isNaN(line[COLUMNS.PRICE]) && !!line[COLUMNS.PRICE]) {
      return ERROR_TYPE.PRICE
    }

    if (line[COLUMNS.VAT] && !this.vatList.includes(line[COLUMNS.VAT])) {
      return ERROR_TYPE.VAT
    }
    if (line[COLUMNS.EAN] && `${line[COLUMNS.EAN]}`?.length > 0 && this.eanMap.get(`${line[COLUMNS.EAN]}`)) {
      return ERROR_TYPE.EAN_DUPLICATE
    }
    return ERROR_TYPE.IS_EVERYTHING_OK
  }

  public import(): void {
    this.isImportInProgress = true
    this.showProgress = true
    this.showSkipMessage = false
    const result = this.transformData(this.xlsxData)
    this.save(result)
  }

  private transformData(data: Array<any>): Array<any> {
    const result = data.map((row, index) => {
      if (!this.errorList.find(value => value.line === index)) {
        const prod = new Product({
          id: row[COLUMNS.PRODUCT_ID],
          name: row[COLUMNS.NAME],
          purchaseCurrency: this.core.services.user.user?.company?.currency,
          vat:
            row[COLUMNS.VAT] !== undefined && !isNaN(Number(row[COLUMNS.VAT]))
              ? { id: this.core.lists.vats.filter(value => value.percent === row[COLUMNS.VAT])[0].id }
              : { id: this.core.lists.vats.filter(value => value.default)[0].id },
          purchasePrice: !isNaN(Number(row[COLUMNS.PRICE])) ? `${Number(row[COLUMNS.PRICE])}` : '0.0',
          sellPrice: '0.0',
          ean: row[COLUMNS.EAN],
          code3: row[COLUMNS.PRODUCER],
          supplier:
            row[COLUMNS.SUPPLIER] !== undefined
              ? { id: this.supplierList.find(value => value.name === `${row[COLUMNS.SUPPLIER]}`)?.id }
              : null,
        })
        if ([null, undefined, ''].includes(row[COLUMNS.PRODUCT_ID])) {
          prod.id = undefined
        }

        return (({ ean, id, name, purchaseCurrency, purchasePrice, sellPrice, supplier, vat, code3 }) => ({
          ean,
          id,
          name,
          purchaseCurrency,
          purchasePrice,
          sellPrice,
          supplier,
          vat,
          code3,
        }))(prod)
      }
    })
    return result
  }

  showInvalidData() {
    this.dialogService.open(InvalidListComponent, {
      header: this.translateService.instant('wizard.invalid-dialog.title'),
      data: {
        errorList: this.errorList,
      },
      style: {
        width: '600px',
        height: 'auto',
      },
    })
  }

  private async save(result: Array<Product>) {
    this.notificationService.avoidErrorAlerts = true
    this.progressValue = 0
    const promList = []
    result.forEach(value => {
      if (value?.name) {
        if (value.id) {
          value.enabled = undefined
          promList.push(() =>
            this.core.services.product.updateProductPart(value).then(_ => {
              this.progressValue += 1
            }),
          )
        } else {
          value.enabled = true
          promList.push(() =>
            this.core.services.product.create(value).then(_ => {
              this.progressValue += 1
            }),
          )
        }
      }
    })
    await this.handlePromises(promList)
    this.notificationService.avoidErrorAlerts = false
    this.importErrorMsgs = this.notificationService.getMessagesWhileAvoidedAlerts()
  }

  async handlePromises(promises: any) {
    for (const promise of promises) {
      await promise()
    }
    this.isImportInProgress = false
    this.showImport = !this.showImport
    this.showResultPage = true
  }

  back(showImport: boolean) {
    if (showImport && !this.showResultPage) {
      this.router.navigate(['/plugplay/step-4'])
    } else {
      this.showStart = false
      this.showProgress = false
      this.showImport = !this.showImport
      this.showResultPage = false
      this.file = null
      this.showValidationProgress = false
      this.isValidationRunning = false
      this.validationProgressValue = 0
      this.importErrorMsgs = []
      this.notificationService.avoidErrorAlerts = false
    }
  }

  next(showImport: boolean) {
    if (showImport) {
      if (this.showResultPage) {
        this.showImport = !this.showImport
      } else {
        this.notificationService.avoidErrorAlerts = false
        this.router.navigate(['/plugplay/step-6'])
        this.setHighestLastStepCompleted.emit(4)
      }
    } else {
      this.notificationService.avoidErrorAlerts = false
      this.setHighestLastStepCompleted.emit(4)
      this.router.navigate(['/plugplay/step-6'])
    }
  }
}

enum ERROR_TYPE {
  NAME = 'wizard.step-5.error.name',
  SUPPLIER = 'wizard.step-5.error.supplier',
  PRICE = 'wizard.step-5.error.price',
  VAT = 'wizard.step-5.error.vat',
  IS_EVERYTHING_OK = 'true',
  PRODUCT = 'wizard.step-5.error.product',
  EAN_DUPLICATE = 'wizard.step-5.error.ean-duplicate',
}

enum COLUMNS {
  NAME = 1,
  SUPPLIER = 4,
  PRICE = 5,
  VAT = 6,
  EAN = 3,
  PRODUCER = 2,
  PRODUCT_ID = 0,
}
